import { AppURLs } from './appURLs';
import axios from 'axios';
import { Modal } from 'antd';
import environment from 'app/config/environment';
import { getFirebaseAccessToken } from './firebase';
import logger from 'app/components/common/ErrorLogger';
import { applyActiveBrandHeader } from 'app/utils/localStorageHandler/brandData';

const apiV2 = axios.create({
  // baseURL: 'https://maxhome.ai/api',
  baseURL: environment.api.API_BASE_URL + '/v2',
});

const pendingRequests = new Map();

const getRequestKey = (config: any) => {
  const { url, method } = config;
  // Remove params
  const baseUrl = url.split('?')[0];
  return `${method}:${baseUrl}`;
};

apiV2.interceptors.request.use(
  async (config: any) => {
    const token = await getFirebaseAccessToken();
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }

    applyActiveBrandHeader(config);

    if (
      config?.method?.toLowerCase() === 'get' &&
      !config?.url?.includes('/user/profile') &&
      !config?.url?.includes('/office') &&
      // Extraction-status polling is called from multiple hooks concurrently
      // (upload banner + transaction page). The dedupe key strips query
      // params so parallel calls with different id sets collide — and because
      // a cancelled request is returned as a never-resolving Promise, the
      // cancelled caller's `await` hangs forever. Skip dedupe here.
      !config?.url?.includes('/documents/status')
    ) {
      const requestKey = getRequestKey(config);

      // Cancel previous requests with the same signature
      const pending = pendingRequests.get(requestKey);
      if (pending) {
        console.log('Cancelling previous request:', requestKey);
        pending.cancel('Request superseded by newer request');
        pendingRequests.delete(requestKey);
      }

      // Create cancel token
      const cancelTokenSource = axios.CancelToken.source();
      config.cancelToken = cancelTokenSource.token;

      // Store the cancel function
      pendingRequests.set(requestKey, {
        cancel: cancelTokenSource.cancel,
      });
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiV2.interceptors.response.use(
  (response) => {
    const requestKey = getRequestKey(response.config);
    pendingRequests.delete(requestKey);
    return response;
  },
  (error) => {
    if (error.config) {
      const requestKey = getRequestKey(error.config);
      pendingRequests.delete(requestKey);
    }

    if (axios.isCancel(error)) {
      console.log('Request cancelled:', error.message);
      return new Promise(() => {});
    }

    const status = error.response?.status;
    const url = error.config?.url ?? 'unknown';
    const method = (error.config?.method ?? 'unknown').toUpperCase();
    logger.log({
      type: 'API_ERROR',
      level: status && status >= 500 ? 'error' : 'warn',
      message: `${method} ${url} → ${status ?? 'network error'}`,
      error: error instanceof Error ? error : undefined,
      tags: {
        endpoint: url,
        method,
        status_code: String(status ?? 'network'),
      },
      metadata: { responseData: error.response?.data },
    });

    if (error.response && error.response.status === 401) {
      localStorage.removeItem('authToken');
      if (error.config.url.includes('/compliance/')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      } else if (error.config.url.includes('/agent/')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      } else if (error.config.url.includes('/office-admin')) {
        window.location.href = AppURLs.agentLoginSessionExpired();
      }
    }

    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.isViewOnly
    ) {
      Modal.warning({
        title: 'View-Only Access',
        content:
          'Your account has view-only access. You can browse and explore the platform, but cannot make any changes.',
      });
    }

    return Promise.reject(error);
  }
);

export default apiV2;
